<template>
  <v-dialog :model-value="true" width="100%" :persistent="true" @update:model-value="onDialogModel">
    <v-card>
      <v-form @submit.prevent="submitBulkEntries">
        <v-row>
          <v-card-title class="pb-0 mt-2 ml-4">{{ $t('entry.bulk_title') }}</v-card-title>
          <v-col cols="12" md="4" class="mt-1">
            <v-text-field
              v-model="searchQuery"
              :label="$t('entry.search_label')"
              density="compact"
              hide-details="true"
              clearable
            />
          </v-col>
        </v-row>

        <v-card-text class="pt-0">
          <div class="bulk-scroll-frame">
            <div v-if="tableLoading" class="bulk-loading-overlay">
              <div class="text-medium-emphasis">{{ $t('entry.bulk_loading_table') }}</div>
            </div>

            <v-virtual-scroll
              v-if="tableReady"
              :items="pagedUserOptions"
              :item-height="rowHeight"
              height="70vh"
              class="bulk-virtual"
            >
              <template #default="{ item: user }">
                <div
                  v-if="bulkEntries[user.value]"
                  class="user-row"
                  :class="selectedBulkUsersSet.has(user.value) ? 'bulk-row-selected' : 'bulk-row-unselected'"
                >
                  <span>
                    <v-checkbox
                      v-model="selectedBulkUsers"
                      density="default"
                      hide-details="true"
                      :value="user.value"
                      class="ml-2 bulk-row-checkbox"
                      :class="selectedBulkUsersSet.has(user.value) ? 'bulk-checkbox-selected' : 'bulk-checkbox-unselected'"
                    />
                  </span>

                  <span class="user-cell user-cell--name">
                    <div class="user-name">{{ user.title }}</div>
                  </span>

                  <span class="user-cell user-si_number" v-if="!event.disable_si_cards">
                    <v-text-field
                      v-model="bulkEntries[user.value].si_number"
                      type="number"
                      :label="$t('entry.si_number_label')"
                      :rules="[rules.siNumber]"
                      density="compact"
                      hide-details="auto"
                    />
                  </span>

                  <span class="px-3" v-if="showSiRent">
                    <v-checkbox
                      v-model="bulkEntries[user.value].si_rent"
                      :label="$t('entry.si_rent_label')"
                      density="compact"
                      hide-details="true"
                    />
                  </span>

                  <span class="user-cell">
                    <v-select
                      :items="categoryOptionsByUser[user.value] || []"
                      item-title="category_name"
                      item-value="id"
                      v-model="bulkEntries[user.value].selectedCategoryRace0"
                      :label="$t('entry.main_category_label')"
                      :no-data-text="$t('entry.no_categories')"
                      density="compact"
                      hide-details="true"
                      @update:model-value="(id) => syncBulkRaceCategories(user.value, id)"
                    />
                  </span>

                  <span class="user-cell">
                    <v-text-field
                      v-model="bulkEntries[user.value].note"
                      maxlength="80"
                      counter
                      :label="$t('entry.note_for_org_label')"
                      density="compact"
                      hide-details="auto"
                    />
                  </span>

                  <div
                    class="user-cell"
                    v-for="race in races"
                    :key="'race-' + race.id + '-user-' + user.value"
                  >
                    <div class="race-select-container">
                      <v-select
                        :items="raceCategoryOptionsByUser[user.value]?.[race.id] || []"
                        item-title="category_name"
                        item-value="id"
                        v-model="bulkSelectedCategories[user.value][race.id]"
                        :label="'Kat. ' + race.race_name"
                        clearable
                        density="compact"
                        hide-details="true"
                      />
                      <v-select
                        v-if="event.allow_start_choice"
                        density="compact"
                        hide-details="true"
                        :items="START_CHOICE_ITEMS"
                        v-model="bulkSelectedStarts[user.value][race.id]"
                        :label="$t('entry.start_choice_label')"
                      />
                    </div>
                  </div>

                  <template v-if="!shouldDisableCreate">
                    <div
                      class="user-cell"
                      v-for="col in accColumns"
                      :key="'bulk-acc-' + user.value + '-' + col.date"
                    >
                      <div class="race-select-container">
                        <h3>{{ col.dateLabel }}</h3>
                        <v-checkbox
                          v-if="col.isSingle"
                          v-model="bulkSelectedAccommodations[user.value][col.date]"
                          :label="col.single.name"
                          :true-value="col.single.id"
                          :false-value="''"
                          density="compact"
                          hide-details="true"
                        />
                        <v-select
                          v-else
                          :items="col.accommodations"
                          item-title="name"
                          item-value="id"
                          v-model="bulkSelectedAccommodations[user.value][col.date]"
                          :label="$t('entry.accommodation_select_label')"
                          clearable
                          density="compact"
                          hide-details="true"
                        />
                      </div>
                    </div>

                    <div
                      class="user-cell"
                      v-for="col in mealColumns"
                      :key="'bulk-meals-' + user.value + '-' + col.date"
                    >
                      <div class="race-select-container">
                        <h3>{{ col.dateLabel }}</h3>
                        <div v-for="slot in col.slots" :key="slot.code">
                          <v-tooltip v-if="slot.isSingle" location="top">
                            <template #activator="{ props: tipProps }">
                              <v-checkbox
                                v-bind="tipProps"
                                v-model="bulkSelectedMeals[user.value][col.date][slot.code]"
                                :label="slot.single.name"
                                :true-value="slot.single.id"
                                :false-value="null"
                                density="compact"
                                hide-details="true"
                              />
                            </template>
                            {{ slot.label }}
                          </v-tooltip>
                          <v-select
                            v-else-if="slot.hasOptions"
                            :items="slot.options"
                            item-title="name"
                            item-value="id"
                            v-model="bulkSelectedMeals[user.value][col.date][slot.code]"
                            :label="slot.label"
                            clearable
                            density="compact"
                            hide-details="true"
                          />
                        </div>
                      </div>
                    </div>

                    <div
                      class="user-cell"
                      v-for="col in otherServiceColumns"
                      :key="'bulk-other-' + user.value + '-' + col.groupKey"
                    >
                      <div class="race-select-container">
                        <h3>{{ $t('entry.service_title') }} #{{ col.index }}</h3>
                        <v-select
                          v-if="col.isMulti"
                          :items="col.variants"
                          item-title="name"
                          item-value="id"
                          v-model="bulkSelectedOther[user.value][col.groupKey]"
                          :label="$t('entry.variant_select_label')"
                          density="compact"
                          clearable
                          hide-details
                        />
                        <v-checkbox
                          v-else
                          v-model="bulkSelectedOther[user.value][col.groupKey]"
                          :true-value="col.single.id"
                          :false-value="false"
                          :label="col.single.name"
                          density="compact"
                          hide-details
                        />
                      </div>
                    </div>
                  </template>
                </div>
              </template>
            </v-virtual-scroll>
          </div>
        </v-card-text>

        <v-alert type="error" v-if="errorMessage">{{ errorMessage }}</v-alert>

        <v-card-actions v-if="$vuetify.display.sm || $vuetify.display.xs">
          <v-btn color="grey-darken-3" text @click="close"><v-icon size="40">mdi-close-circle</v-icon></v-btn>
          <v-spacer />
          <v-pagination v-model="currentPage" :length="pageCount" :total-visible="0" :disabled="tableLoading" />
          <v-spacer />
          <v-btn type="submit" color="success" :loading="isFormLoading" :disabled="tableLoading">
            <v-icon size="40">mdi-send-circle</v-icon>
          </v-btn>
        </v-card-actions>

        <v-card-actions v-else>
          <v-btn color="grey-darken-3" text @click="close">{{ $t('general.cancel') }}</v-btn>
          <v-spacer />
          <v-pagination v-model="currentPage" :length="pageCount" :total-visible="5" :disabled="tableLoading" />
          <v-spacer />
          <v-btn
            type="submit"
            color="success"
            :loading="isFormLoading"
            :disabled="siNumberHasError || tableLoading"
          >
            {{ $t('entry.submit_btn') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { format, parseISO } from 'date-fns'
import apiClient from '/api.js'
import { useI18n } from 'vue-i18n'
import { useSnackbarStore } from '@/stores/snackbarStore'
import { START_CHOICE_ITEMS } from '@/components/const/enums.js'
import {
  calculateUserAge,
  isUserInSameClub,
  filterCategoriesByAgeAndSex,
} from '@/utils/entryCategories.js'

const props = defineProps({
  users: { type: Array, required: true },
  event: { type: Object, required: true },
  races: { type: Array, required: true },
  race0: { type: Object, required: true },
  accommodationsByDate: { type: Object, default: () => ({}) },
  mealsByDate: { type: Object, default: () => ({}) },
  groupedOtherServices: { type: Object, default: () => ({}) },
  siRentData: { type: Object, default: null },
  shouldDisableCreate: { type: Boolean, default: false },
  eventId: { type: [String, Number], required: true },
})

const emit = defineEmits(['close', 'success'])

const { t } = useI18n()
const snackbar = useSnackbarStore()

const FOOD_ORDER = ['sn', 'ob', 've']
const foodLabel = (code) =>
  ({ sn: t('entry.breakfast'), ob: t('entry.lunch'), ve: t('entry.dinner') }[code] || code)

const rules = {
  siNumber: (value) => !value || /^\d{4,7}$/.test(String(value)) || t('rules.si_range'),
}

const itemsPerPage = 20
const currentPage = ref(1)
const searchQuery = ref('')
const debouncedSearchQuery = ref('')
let debounceTimeout = null
let loadToken = 0

const tableLoading = ref(true)
const tableReady = ref(false)

const selectedBulkUsers = ref([])
const selectedBulkUsersSet = computed(() => new Set(selectedBulkUsers.value))
const bulkEntries = reactive({})
const bulkSelectedCategories = reactive({})
const bulkSelectedStarts = reactive({})
const bulkSelectedAccommodations = reactive({})
const bulkSelectedMeals = reactive({})
const bulkSelectedOther = reactive({})

const errorMessage = ref('')
const isFormLoading = ref(false)

const showSiRent = computed(
  () => !props.event.disable_si_cards && props.siRentData && props.siRentData.price !== null
)

const usersById = computed(() => {
  const map = new Map()
  for (const user of props.users) map.set(user.id, user)
  return map
})

const userOptions = computed(() =>
  props.users.map((user) => ({
    title: `${user.last_name} ${user.first_name}${user.competitor_index ? ` (${user.competitor_index})` : ''}`,
    value: user.id,
  }))
)

const filteredUserOptions = computed(() => {
  if (!debouncedSearchQuery.value || debouncedSearchQuery.value.length < 3) {
    return userOptions.value
  }
  const q = debouncedSearchQuery.value.toLowerCase()
  return userOptions.value.filter((user) => user.title.toLowerCase().includes(q))
})

const pageCount = computed(() =>
  Math.max(1, Math.ceil((filteredUserOptions.value.length || 1) / itemsPerPage))
)

const pagedUserOptions = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  return filteredUserOptions.value.slice(start, start + itemsPerPage)
})

const accColumns = computed(() =>
  Object.entries(props.accommodationsByDate || {}).map(([date, accommodations]) => ({
    date,
    accommodations,
    isSingle: accommodations.length === 1,
    single: accommodations.length === 1 ? accommodations[0] : null,
    dateLabel: format(parseISO(date), 'd.M.'),
  }))
)

const mealColumns = computed(() =>
  Object.entries(props.mealsByDate || {}).map(([date, meals]) => ({
    date,
    dateLabel: format(parseISO(date), 'd.M.'),
    slots: FOOD_ORDER.map((code) => {
      const options = meals.filter((m) => m.type === code)
      return {
        code,
        options,
        isSingle: options.length === 1,
        single: options.length === 1 ? options[0] : null,
        hasOptions: options.length > 1,
        label: foodLabel(code),
      }
    }),
  }))
)

const otherServiceColumns = computed(() =>
  Object.entries(props.groupedOtherServices || {}).map(([groupKey, variants], i) => ({
    groupKey,
    variants,
    index: i + 1,
    isMulti: variants.length > 1,
    single: variants.length === 1 ? variants[0] : null,
  }))
)

const rowHeight = computed(() => {
  let stack = 1
  if (props.event.allow_start_choice) stack = Math.max(stack, 2)
  for (const col of mealColumns.value) {
    const visibleSlots = col.slots.filter((s) => s.isSingle || s.hasOptions).length
    stack = Math.max(stack, 1 + visibleSlots)
  }
  if (accColumns.value.length) stack = Math.max(stack, 2)
  if (otherServiceColumns.value.length) stack = Math.max(stack, 2)
  return Math.max(80, 16 + stack * 48)
})

const categoryOptionsByUser = computed(() => {
  const acc = {}
  const races0 = props.event?.races?.[0]
  if (!races0) return acc
  for (const user of props.users) {
    const age = calculateUserAge(user.birth_year)
    const isSameClub = isUserInSameClub(user.competitor_club, props.event.club)
    acc[user.id] = filterCategoriesByAgeAndSex(races0.categories, age, user.competitor_gender, isSameClub)
  }
  return acc
})

const raceCategoryOptionsByUser = computed(() => {
  const acc = {}
  if (!props.event?.races?.length) return acc
  for (const user of props.users) {
    const age = calculateUserAge(user.birth_year)
    const isSameClub = isUserInSameClub(user.competitor_club, props.event.club)
    acc[user.id] = {}
    for (const race of props.event.races) {
      acc[user.id][race.id] = filterCategoriesByAgeAndSex(
        race.categories,
        age,
        user.competitor_gender,
        isSameClub
      )
    }
  }
  return acc
})

const siNumberHasError = computed(() =>
  Object.values(bulkEntries).some((entry) => rules.siNumber(entry.si_number) !== true)
)

function syncBulkRaceCategories(userId, selectedCategoryRace0) {
  if (!selectedCategoryRace0) return
  const selectedCategory = (categoryOptionsByUser.value[userId] || []).find(
    (cat) => cat.id === selectedCategoryRace0
  )
  if (!selectedCategory) return
  const selectedCategoryName = selectedCategory.category_name
  if (!bulkSelectedCategories[userId]) bulkSelectedCategories[userId] = {}
  for (const race of props.races) {
    const matching = (raceCategoryOptionsByUser.value[userId]?.[race.id] || []).find(
      (cat) => cat.category_name === selectedCategoryName
    )
    if (matching) bulkSelectedCategories[userId][race.id] = matching.id
  }
}

function ensureUserState(userId) {
  if (bulkEntries[userId]) return
  const user = usersById.value.get(userId)
  if (!user) return

  bulkEntries[userId] = {
    si_number: user.si_number != null ? user.si_number : '',
    note: '',
    si_rent: false,
    selectedCategoryRace0: null,
  }
  bulkSelectedCategories[userId] = {}
  bulkSelectedStarts[userId] = {}
  for (const race of props.races) {
    bulkSelectedCategories[userId][race.id] = null
    bulkSelectedStarts[userId][race.id] = ''
  }
  bulkSelectedAccommodations[userId] = {}
  for (const date of Object.keys(props.accommodationsByDate || {})) {
    bulkSelectedAccommodations[userId][date] = ''
  }
  bulkSelectedMeals[userId] = {}
  for (const date of Object.keys(props.mealsByDate || {})) {
    bulkSelectedMeals[userId][date] = {}
    for (const code of FOOD_ORDER) {
      bulkSelectedMeals[userId][date][code] = null
    }
  }
  bulkSelectedOther[userId] = {}
  for (const key of Object.keys(props.groupedOtherServices || {})) {
    bulkSelectedOther[userId][key] = null
  }

  const cats = categoryOptionsByUser.value[userId] || []
  if (cats.length) {
    bulkEntries[userId].selectedCategoryRace0 = cats[0].id
    syncBulkRaceCategories(userId, cats[0].id)
  }
}

async function loadTable() {
  const token = ++loadToken
  tableLoading.value = true
  tableReady.value = false

  for (const user of pagedUserOptions.value) ensureUserState(user.value)

  // nejdřív vykresli overlay, teprve potom mount tabulky
  await nextTick()
  await new Promise((resolve) => requestAnimationFrame(resolve))
  if (token !== loadToken) return

  tableReady.value = true
  await nextTick()
  await new Promise((resolve) => requestAnimationFrame(resolve))
  if (token !== loadToken) return

  tableLoading.value = false
}

function close() {
  loadToken += 1
  emit('close')
}

function onDialogModel(val) {
  if (!val) close()
}

async function submitBulkEntries() {
  for (const userId of selectedBulkUsers.value) {
    ensureUserState(userId)
    const user = usersById.value.get(userId)
    const userData = bulkEntries[userId]
    if (!userData.selectedCategoryRace0) {
      errorMessage.value = t('entry.must_select_main_category', {
        lastName: user.last_name,
        firstName: user.first_name,
      })
      return
    }
    if (!props.event.disable_si_cards) {
      if ((!userData.si_number || userData.si_number === '') && !userData.si_rent) {
        errorMessage.value = t('entry.bulk_chip_or_rent_required', {
          first: user.first_name,
          last: user.last_name,
        })
        return
      }
    }
  }

  isFormLoading.value = true
  const bulkRaceEntries = []

  selectedBulkUsers.value.forEach((userId) => {
    const user = usersById.value.get(userId)
    if (!user) return
    const userData = bulkEntries[userId]
    const baseEntry = {
      id: user.id,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      index: user.index,
      competitor_index: user.competitor_index,
      competitor_club: user.competitor_club,
      competitor_gender: user.competitor_gender,
      birth_year: user.birth_year,
      gender: user.gender,
      country: user.country,
      si_number: userData.si_number === '' ? null : userData.si_number,
      note: userData.note,
    }

    if (userData.selectedCategoryRace0) {
      const race_services = []
      if (userData.si_rent && props.siRentData) {
        race_services.push(props.siRentData.id)
      }
      Object.values(bulkSelectedAccommodations[userId] || {}).forEach((accId) => {
        if (accId) race_services.push(accId)
      })
      Object.values(bulkSelectedMeals[userId] || {}).forEach((mealsForDate) => {
        Object.values(mealsForDate).forEach((mealId) => {
          if (mealId) race_services.push(mealId)
        })
      })
      Object.values(bulkSelectedOther[userId] || {}).forEach((svcId) => {
        if (svcId) race_services.push(Number(svcId))
      })

      bulkRaceEntries.push({
        ...baseEntry,
        race: props.race0.id,
        competitor_category: userData.selectedCategoryRace0,
        race_services,
      })
    }

    props.races.forEach((race) => {
      const selectedCat = bulkSelectedCategories[userId]?.[race.id]
      if (selectedCat) {
        bulkRaceEntries.push({
          ...baseEntry,
          race: race.id,
          competitor_category: selectedCat,
          start_preference: bulkSelectedStarts[userId]?.[race.id] || '',
          race_services: [],
        })
      }
    })
  })

  try {
    await apiClient.post(`event/${props.eventId}/entry/create/`, { races: bulkRaceEntries })
    snackbar.success(t('entry.create_bulk_success'))
    errorMessage.value = ''
    emit('success')
    close()
  } catch (error) {
    console.error('Chyba při odesílání hromadné přihlášky:', error)
    if (error.response?.status === 423) {
      errorMessage.value = error.response.data.error || t('entry.limit_accommodation_exhausted')
    } else if (error.response?.status === 409) {
      errorMessage.value = error.response.data.error
    } else {
      errorMessage.value = t('entry.create_failed')
    }
  } finally {
    isFormLoading.value = false
  }
}

watch(searchQuery, (newVal) => {
  if (debounceTimeout) clearTimeout(debounceTimeout)
  debounceTimeout = setTimeout(() => {
    debouncedSearchQuery.value = newVal
    currentPage.value = 1
  }, 300)
})

watch([currentPage, debouncedSearchQuery], () => loadTable(), { immediate: true })

onBeforeUnmount(() => {
  loadToken += 1
  if (debounceTimeout) clearTimeout(debounceTimeout)
})
</script>

<style scoped>
.bulk-scroll-frame {
  position: relative;
  width: 100%;
  min-height: 70vh;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
.bulk-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-surface), 0.72);
  backdrop-filter: blur(1px);
}
.bulk-virtual {
  width: 100% !important;
  overflow-x: auto !important;
  overflow-y: scroll !important;
  scrollbar-gutter: stable;
}
.bulk-virtual :deep(.v-virtual-scroll__container) {
  min-width: max-content;
}
.user-row {
  display: flex;
  align-items: center;
  min-width: max-content;
  width: 100%;
  padding: 3px;
  overflow: hidden;
  box-sizing: border-box;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background-color: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
}
.bulk-row-selected {
  background-color: rgb(var(--v-theme-bulk-row-selected)) !important;
}
.bulk-row-unselected {
  background-color: rgb(var(--v-theme-surface)) !important;
}
.bulk-checkbox-selected {
  background-color: rgb(var(--v-theme-bulk-checkbox-on)) !important;
  border-radius: 4px;
}
.bulk-checkbox-unselected {
  background-color: rgb(var(--v-theme-bulk-checkbox-off)) !important;
  border-radius: 4px;
}
.user-cell {
  flex: 0 0 auto;
  width: 200px;
  padding: 4px 6px;
  display: flex;
  align-items: stretch;
}
.user-cell--name {
  width: 220px;
  align-items: center;
}
.user-si_number {
  width: 130px !important;
}
.user-name {
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.race-select-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 3px;
}
@media (max-width: 600px) {
  .bulk-scroll-frame {
    min-height: 65vh;
  }
  .bulk-virtual {
    height: 65vh !important;
  }
}
</style>
